import React from 'react';
import { Link } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { num } from '../../contexts/FarmStatusContext';
import { defaultRanges, breachDirection, statusColor } from '../../config/sensorRanges';
import { Panel, PanelTitle } from './ui';
import { alpha, ago } from './helpers';

const Muted = styled.span`color: ${p => p.theme.colors.textMuted};`;

const TankGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(128px, 1fr));
  gap: 10px;

  @media (max-width: 560px) { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
`;

const TankTile = styled(Link)`
  display: block;
  min-width: 0;
  min-height: 0;
  padding: 12px 14px 12px;
  border-radius: 12px;
  border: 1px solid ${p => (p.$flag ? alpha(statusColor(p.theme, p.$flag), 0.6) : p.theme.colors.border)};
  background: ${p => (p.$flag ? alpha(statusColor(p.theme, p.$flag), 0.05) : p.theme.colors.surface)};
  color: inherit;
  text-decoration: none;
  opacity: ${p => (p.$dim ? 0.45 : 1)};
  transition: border-color 0.15s ease;

  &:hover { border-color: ${p => p.theme.colors.borderHover}; }
  &:focus-visible { outline: 2px solid ${p => p.theme.colors.primary}; outline-offset: 2px; }

  /* Name over the reading's age, so a long name (Aero Tower) is never cut in a narrow tile. */
  .top { display: flex; flex-direction: column; gap: 0; }
  .nm { font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .age { font-size: 12px; color: ${p => p.theme.colors.textMuted}; white-space: nowrap; }
  .age.late { color: ${p => p.theme.colors.text}; font-weight: 500; }
  .vals { margin-top: 10px; display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .lab { font-size: 11px; color: ${p => p.theme.colors.textMuted}; }
  .val { font-size: 22px; font-weight: 500; letter-spacing: -0.03em; line-height: 1.15; white-space: nowrap; }
  .val em { font-style: normal; font-size: 11px; font-weight: 500; letter-spacing: 0; padding: 1px 4px; border-radius: 3px; vertical-align: 4px; margin-left: 4px; }
  .water { margin-top: 10px; padding-top: 8px; border-top: 1px solid ${p => p.theme.colors.border}; font-size: 12px; color: ${p => p.theme.colors.textSecondary}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .probe { margin-top: 4px; font-size: 11px; color: ${p => p.theme.colors.textMuted}; }
`;

const Flag = styled.em`
  color: ${p => p.theme.colors.text};
  background: ${p => alpha(statusColor(p.theme, p.$status), 0.25)};
`;

const Late = styled.span`
  color: ${p => (p.$status === 'critical' ? p.theme.colors.critical : p.theme.colors.text)};
  font-weight: ${p => (p.$status ? 600 : 400)};
`;

/* Where a reading sits: the safe (warning) band tinted, a dot for the value. */
function Gauge({ sensor, value, level, ranges }) {
  const theme = useTheme();
  const r = ranges?.[sensor] || defaultRanges[sensor];
  if (value == null || !r?.warning || !r?.critical) return null;
  const lo = r.critical.min, hi = r.critical.max;
  const pad = (hi - lo) * 0.08;
  const pos = v => Math.max(0, Math.min(100, ((v - (lo - pad)) / (hi - lo + 2 * pad)) * 100));
  const a = pos(r.warning.min), b = pos(r.warning.max);
  const c = statusColor(theme, level || 'normal');
  return (
    <svg viewBox="0 0 100 8" preserveAspectRatio="none" width="100%" height="8" style={{ display: 'block', marginTop: 6 }} aria-hidden="true">
      <rect x="0" y="3" width="100" height="2" rx="1" fill={theme.colors.border} />
      <rect x={a} y="3" width={b - a} height="2" fill={alpha(theme.colors.success, 0.55)} />
      <circle cx={pos(value)} cy="4" r="3.2" fill={c} stroke={theme.colors.surface} strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/* One tile per tank: manual pH and EC against the safe band, checked age, water change due or late. */
export default function TanksCard({ tanks, ranges, water, now, order }) {
  return (
    <Panel $order={order}>
      <PanelTitle>Tanks <Link to="/manual-entry">Log a reading</Link></PanelTitle>
      <TankGrid>
        {tanks.map(t => (
          <TankTile key={t.id} to="/manual-entry" $dim={t.maintenance} $flag={t.phLevel || t.ecLevel || null}>
            <div className="top">
              <span className="nm">{t.name}</span>
              <span className={`age${t.current ? '' : ' late'}`}>
                {t.maintenance ? 'maintenance' : t.lastAt ? ago(t.lastAt, now) : 'never'}
              </span>
            </div>
            <div className="vals">
              {[['ph', 'pH', t.ph, t.phLevel, v => v.toFixed(1)], ['ec', 'EC', t.ec, t.ecLevel, v => v.toFixed(2)]].map(([key, label, v, level, f]) => (
                <div key={key}>
                  <div className="lab">{label}</div>
                  <div className="val">
                    {v !== null ? f(v) : '-'}
                    {level && <Flag $status={level}>{breachDirection(key, v, ranges) === 'above' ? 'HIGH' : 'LOW'}</Flag>}
                  </div>
                  {t.current && <Gauge sensor={key} value={v} level={level} ranges={ranges} />}
                </div>
              ))}
            </div>
            {t.probe && (
              <div className="probe">
                Probe pH {num(t.probe.ph)?.toFixed(1) ?? '-'}
                {num(t.probe.water_temp) !== null && `, ${t.probe.water_temp.toFixed(1)} °C`}
              </div>
            )}
            <div className="water">
              {!t.water?.last_water_change ? <Muted>No water change yet</Muted>
                : t.water.is_overdue
                  ? <Late $status={t.water.days_since_change >= (t.water.interval_days || water?.interval_days || 14) * 2 ? 'critical' : 'warning'}>Water {t.water.days_since_change - (t.water.interval_days || water?.interval_days || 14)} d late</Late>
                  : <>Water change in {t.water.days_until_due} d</>}
            </div>
          </TankTile>
        ))}
      </TankGrid>
    </Panel>
  );
}
